import { useState } from "react";
import HeroImage from "../../assets/png/ZionNationalPark.png";
import SEOMetadata from "../../components/SEOMetadata";
import { seoConfig } from "../../config/seoConfig";
import ContactFormModal from "../../components/header/HomeHeader/contact/ContactFormModal";

const ATTRIBUTES = [
  {
    label: "Passion",
    blurb: "You believe in the product and the opportunity",
  },
  {
    label: "Entrepreneurship",
    blurb: "You're proactive and self-motivated",
  },
  {
    label: "Sales skills",
    blurb: "You know how to convert interest into action",
  },
  {
    label: "Business savvy",
    blurb: "You can manage and grow a profitable operation",
  },
];

const WHO_ITS_FOR = [
  "Entrepreneurs looking for a scalable business",
  "Automotive enthusiasts ready to monetize their passion",
  "Existing operators wanting to expand their portfolio",
  "Sales-driven individuals who thrive in digital environments",
];

const BecomeADealer = () => {
  const seo = seoConfig.us.becomeADealer;
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <div className="bg-white">
      <SEOMetadata
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />

      {/* ── Hero Banner ── */}
      <div className="relative w-full overflow-hidden min-h-[420px] sm:min-h-[500px] md:min-h-[600px] lg:min-h-[650px]">
        <img
          src={HeroImage}
          alt="Become a Tuatara Dealer"
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center px-3 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10">
          <div className="bg-[rgba(0,0,0,0.55)] px-4 py-5 sm:px-8 sm:py-8 md:px-10 md:py-10 w-full max-w-[1000px] text-center rounded-xl text-white">
            <h1 className="font-[Rockwell] text-white font-bold 2xl:text-[30px] xl:text-[30px] lg:text-[28px] md:text-[24px] sm:text-[20px] text-[15px] leading-tight text-center uppercase">
              Become a Tuatara Dealer
            </h1>
            <div className="flex items-center justify-center mt-[5px] mb-3 sm:mb-[20px]">
              <div className="bg-[#fff] sm:h-[8px] h-[4px] sm:w-[350px] w-[120px]"></div>
            </div>
            <p className="font-[Roboto] text-white font-bold lg:text-[18px] md:text-[16px] sm:text-[14px] text-[12px] leading-relaxed text-center mb-3 sm:mb-4">
              Everyone Wants a Tuatara! They Just Need You to Sell Them One.
            </p>
            <p className="font-[Roboto] text-white font-bold lg:text-[18px] md:text-[16px] sm:text-[14px] text-[12px] leading-relaxed text-center mb-5 sm:mb-6">
              Step into a high-demand, low-overhead business opportunity with{" "}
              <strong>Tuatara USA</strong>. If you've got the drive, the
              mindset, and the ambition, we'll provide the product.
            </p>
            <button
              onClick={() => setShowContactForm(true)}
              className="bg-[#6FBF4A] text-white px-5 py-2 sm:px-5 sm:py-3 text-[12px] sm:text-sm md:text-base font-semibold uppercase hover:opacity-90 transition"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="custom-container md:px-[80px] px-[20px] md:py-[80px] sm:py-[60px] py-[40px]">
        {/* The Opportunity */}
        <section className="mb-[64px]">
          <div className="flex items-center gap-[14px] mb-[24px]">
            <div className="w-[5px] h-[30px] sm:h-[36px] bg-[#7ABF5B] flex-shrink-0" />
            <h2 className="font-[Roboto] 2xl:text-[32px] xl:text-[28px] lg:text-[24px] sm:text-[22px] text-[20px] font-bold text-[#292929]">
              The Opportunity
            </h2>
          </div>
          <div className="bg-[#F8F8F8] border-l-[4px] border-[#7ABF5B] md:px-[40px] sm:px-[28px] px-[20px] py-[30px] sm:py-[36px] space-y-[18px]">
            <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.85]">
              You don't need a storefront, just premises of suitable size to
              store up to six Tuatara vehicles &amp; parts with room and
              equipment for assembly. The premises must be securable.
            </p>
            <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.85]">
              This is an internet-based business, so you need an SEO optimized
              website that is configured to accept enquiries. You must also have
              a good quality CRM to capture customer details and manage sales
              and enquiries.
            </p>
            <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.85]">
              Finally, you must have sufficient capital, or access to capital,
              to purchase your first consignment of vehicles and parts.
            </p>
          </div>

          {/* Why Become a Tuatara Dealer? */}
          <div className="mt-[36px] md:px-[8px]">
            <h3 className="font-[Roboto] 2xl:text-[24px] xl:text-[22px] lg:text-[20px] sm:text-[18px] text-[17px] font-bold text-[#292929] mb-[14px]">
              Why Become a Tuatara Dealer?
            </h3>
            <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.85]">
              Tuatara is built for modern distribution &ndash; no expensive
              showroom required. This is a streamlined, internet-driven business
              model designed for entrepreneurial operators who want flexibility
              and scalability.
            </p>
          </div>
        </section>

        {/* Required Attributes */}
        <section className="mb-[64px]">
          <div className="flex items-center gap-[14px] mb-[24px]">
            <div className="w-[5px] h-[30px] sm:h-[36px] bg-[#7ABF5B] flex-shrink-0" />
            <h2 className="font-[Roboto] 2xl:text-[32px] xl:text-[28px] lg:text-[24px] sm:text-[22px] text-[20px] font-bold text-[#292929]">
              Required Attributes
            </h2>
          </div>

          <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.85] mb-[28px]">
            In order of importance, these are the only attributes that you need
            to have a very successful Tuatara business:
          </p>

          <ol className="list-decimal pl-[28px] sm:pl-[36px] space-y-[12px] marker:text-[#7ABF5B] marker:font-bold">
            {ATTRIBUTES.map((a) => (
              <li
                key={a.label}
                className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.75] pl-[6px]"
              >
                <span className="font-bold text-[#292929]">{a.label}</span>
                {" - "}
                {a.blurb}
              </li>
            ))}
          </ol>
        </section>

        {/* Who This Is For */}
        <section className="mb-[64px]">
          <div className="flex items-center gap-[14px] mb-[24px]">
            <div className="w-[5px] h-[30px] sm:h-[36px] bg-[#7ABF5B] flex-shrink-0" />
            <h2 className="font-[Roboto] 2xl:text-[32px] xl:text-[28px] lg:text-[24px] sm:text-[22px] text-[20px] font-bold text-[#292929]">
              Who This Is For
            </h2>
          </div>

          <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.85] mb-[24px]">
            This opportunity is ideal for:
          </p>

          <ul className="list-disc pl-[28px] sm:pl-[36px] space-y-[10px] marker:text-[#7ABF5B]">
            {WHO_ITS_FOR.map((item) => (
              <li
                key={item}
                className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#333] leading-[1.75] pl-[6px]"
              >
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Apply to Become a Tuatara Dealer */}
        <section
          id="apply"
          className="mb-[48px] bg-[#292929] md:px-[56px] sm:px-[36px] px-[24px] md:py-[56px] sm:py-[44px] py-[36px]"
        >
          <h2 className="font-[Roboto] 2xl:text-[32px] xl:text-[28px] lg:text-[24px] sm:text-[22px] text-[20px] font-bold text-white mb-[12px]">
            Apply to Become a Tuatara Dealer
          </h2>
          <div className="w-[60px] h-[4px] bg-[#7ABF5B] mb-[20px]" />
          <p className="font-[Roboto] 2xl:text-[20px] xl:text-[18px] lg:text-[17px] sm:text-[16px] text-[15px] text-[#E5E5E5] leading-[1.75] mb-[28px] max-w-[720px]">
            Start your application today and take the first step toward
            building your Tuatara business.
          </p>
          <button
            // onClick={() => setShowContactForm(true)}
            className="bg-[#7ABF5B] hover:bg-[#609a45] text-white font-[Roboto] font-bold 2xl:text-[18px] xl:text-[16px] text-[14px] uppercase tracking-[1.5px] py-[14px] px-[40px] transition-colors"
            style={{ boxShadow: "0px 4px 4px 0px #00000040" }}
          >
            Click to Apply
          </button>
        </section>

        {/* Have Questions? Get in Touch */}
        <section className="bg-[#F8F8F8] border-[1px] border-[#E8E8E8] md:px-[56px] sm:px-[36px] px-[24px] md:py-[44px] sm:py-[36px] py-[32px] flex flex-col md:flex-row md:items-center md:justify-between gap-[24px]">
          <div>
            <h3 className="font-[Roboto] 2xl:text-[26px] xl:text-[22px] lg:text-[20px] sm:text-[19px] text-[17px] font-bold text-[#292929] mb-[8px]">
              Have Questions? Get in Touch
            </h3>
            <p className="font-[Roboto] 2xl:text-[18px] xl:text-[17px] lg:text-[16px] text-[15px] text-[#555] leading-[1.6]">
              Speak with our team to learn more about the opportunity before
              applying.
            </p>
          </div>
          <button
            onClick={() => setShowContactForm(true)}
            className="flex-shrink-0 bg-[#292929] hover:bg-[#7ABF5B] text-white font-[Roboto] font-bold 2xl:text-[18px] xl:text-[16px] text-[14px] uppercase tracking-[1.5px] py-[14px] px-[40px] transition-colors"
            style={{ boxShadow: "0px 4px 4px 0px #00000040" }}
          >
            Contact Us
          </button>
        </section>
      </div>

      <ContactFormModal
        open={showContactForm}
        handleClose={() => setShowContactForm(false)}
      />
    </div>
  );
};

export default BecomeADealer;
